import React from "react";
import { useLocalization } from "@i18n";
import { GoogleMap } from "../organisms";
import { Button } from "@telluscompany/design-system";
import { useUserContext } from "@shared/contexts";
import { GS3HeaderLogo } from "@/assets/images";
import { LanguageSwitcher } from "@i18n";
import { ModuleCard } from "../molecules";
import {
  Home,
  Activity,
  ClipboardList,
  Target,
  TrendingUp,
  BookOpen,
  ShieldCheck,
  Shield,
  Globe,
  AlertTriangle,
  Waves,
  AlertCircle,
  FileBarChart,
  LayoutDashboard,
  Wrench,
  Settings,
  LockKeyhole,
  Settings2Icon,
} from "lucide-react";

const GS3_COORDINATES = {
  lat: -19.9693551,
  lng: -43.9541795,
};

const MODULES = [
  {
    icon: LockKeyhole,
    key: "authenticationAndSecurity",
    progress: 100,
    isActive: true,
    isCompleted: true,
  },
  {
    icon: Settings,
    key: "managerial",
    progress: 100,
    isActive: true,
    isCompleted: true,
  },
  {
    icon: Settings2Icon,
    key: "administrative",
    progress: 100,
    isActive: true,
    isCompleted: true,
  },
  {
    icon: Home,
    key: "general",
    progress: 100,
    isActive: true,
    isCompleted: true,
  },
  {
    icon: ClipboardList,
    key: "inspections",
    progress: 0,
    isActive: false,
    isCompleted: false,
  },
  {
    icon: Activity,
    key: "monitoring",
    progress: 0,
    isActive: false,
    isCompleted: false,
  },
  {
    icon: BookOpen,
    key: "eor",
    progress: 0,
    isActive: false,
    isCompleted: false,
  },
  {
    icon: ShieldCheck,
    key: "ato",
    progress: 0,
    isActive: false,
    isCompleted: false,
  },
  {
    icon: Shield,
    key: "psb",
    progress: 0,
    isActive: false,
    isCompleted: false,
  },
  {
    icon: Globe,
    key: "gistm",
    progress: 0,
    isActive: false,
    isCompleted: false,
  },
  {
    icon: Target,
    key: "actionPlan",
    progress: 0,
    isActive: false,
    isCompleted: false,
  },
  {
    icon: TrendingUp,
    key: "stability",
    progress: 0,
    isActive: false,
    isCompleted: false,
  },
  {
    icon: AlertTriangle,
    key: "paebm",
    progress: 0,
    isActive: false,
    isCompleted: false,
  },
  {
    icon: Waves,
    key: "damBreak",
    progress: 0,
    isActive: false,
    isCompleted: false,
  },
  {
    icon: AlertCircle,
    key: "riskAnalysis",
    progress: 0,
    isActive: false,
    isCompleted: false,
  },
  {
    icon: FileBarChart,
    key: "report",
    progress: 0,
    isActive: false,
    isCompleted: false,
  },
  {
    icon: LayoutDashboard,
    key: "dashboard",
    progress: 0,
    isActive: false,
    isCompleted: false,
  },
  {
    icon: Wrench,
    key: "cmg",
    progress: 0,
    isActive: false,
    isCompleted: false,
  },
];

const RoadmapPage: React.FC = () => {
  const { translate } = useLocalization();
  const { logout } = useUserContext();

  return (
    <div className="h-full overflow-y-auto flex flex-col">
      {/* Hero */}
      <section className="flex flex-col items-center justify-center px-12 pt-12 pb-8 gap-4">
        <img
          src={GS3HeaderLogo}
          alt="GS3 Logo"
          className="h-12 object-contain"
        />
        <div className="flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
          </span>
          <span className="text-xs font-medium">
            {translate("home.hero.badge")}
          </span>
        </div>
        <LanguageSwitcher />
        <div className="text-center max-w-8xl">
          <h1 className="text-h1 text-foreground font-bold leading-tight">
            {translate("home.hero.tagline")}
          </h1>
          <p className="text-body-large text-muted-foreground mt-3">
            {translate("home.hero.subtitle")}
          </p>
        </div>
      </section>

      {/* Modules Grid */}
      <section className="px-12 pb-8">
        <h2 className="text-h3 text-foreground font-semibold mb-4">
          {translate("home.modules.title")}
        </h2>
        <div className="grid grid-cols-4 gap-4">
          {MODULES.map((module) => (
            <ModuleCard
              key={module.key}
              icon={module.icon}
              name={translate(`home.modules.${module.key}.name`)}
              description={translate(`home.modules.${module.key}.description`)}
              progress={module.progress}
              isActive={module.isActive}
              isCompleted={module.isCompleted}
            />
          ))}
        </div>
      </section>

      {/* Map */}
      <section className="px-12 pb-8">
        <GoogleMap
          lat={GS3_COORDINATES.lat}
          lng={GS3_COORDINATES.lng}
          zoom={15}
        />
      </section>

      {/* Footer */}
      <footer className="mt-auto border-t border-border-1 px-12 py-4 flex justify-end items-center gap-4">
        <Button onClick={async () => await logout()}>
          {translate("auth.logout")}
        </Button>
      </footer>
    </div>
  );
};

export default RoadmapPage;
